// src/components/SendNotificationModal/SendNotificationModal.js
import React, { useState, useEffect, useRef } from "react";
import { Modal, Input, Select, message, Spin } from "antd";
import { useMutation } from "@apollo/client";
import { CREATE_NEW_WEB_NOTIFICATION } from "../services/userAPI";

const { TextArea } = Input;
const { Option } = Select;

// SINGLETON Speech Recognition
let recognition = null;
if (typeof window !== "undefined") {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
        recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
    }
}

const languages = {
    "en-IN": "English (India)",
    "hi-IN": "हिंदी (Hindi)",
    "te-IN": "తెలుగు (Telugu)",
    "kn-IN": "ಕನ್ನಡ (Kannada)",
    "ta-IN": "தமிழ் (Tamil)",
    // "ml-IN": "മലയാളം (Malayalam)",
};

// Compact Voice Visualizer for TextArea
const VoiceVisualizer = ({ isListening }) => {
    const [bars, setBars] = useState([0.4, 0.6, 0.8, 0.6, 0.4]);
    const animationRef = useRef(null);

    useEffect(() => {
        if (isListening) {
            const animate = () => {
                setBars(prev => prev.map(() => Math.random() * 0.5 + 0.5));
                animationRef.current = requestAnimationFrame(animate);
            };
            animate();
        } else {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            setBars([0.4, 0.6, 0.8, 0.6, 0.4]);
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [isListening]);

    return (
        <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "2px",
            height: "20px",
            marginRight: "8px"
        }}>
            {bars.map((height, i) => (
                <div
                    key={i}
                    style={{
                        width: "2.5px",
                        height: `${height * 100}%`,
                        backgroundColor: isListening ? "#AD0101" : "#d9d9d9",
                        borderRadius: "2px",
                        transition: isListening ? "height 0.08s ease" : "height 0.3s ease, background-color 0.3s ease",
                    }}
                />
            ))}
        </div>
    );
};

// Compact Mic Button for TextArea
const MicButton = ({ isListening, onClick, disabled }) => {
    return (
        <div
            onClick={disabled ? undefined : onClick}
            style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: isListening
                    ? "linear-gradient(135deg, #AD0101 0%, #AD0101 100%)"
                    : "linear-gradient(135deg, #AD0101 0%, #BCBDC0 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.4 : 1,
                transition: "all 0.3s ease",
                // boxShadow: isListening
                //     ? "0 3px 10px rgba(255, 77, 79, 0.4)"
                //     : "0 3px 10px rgba(24, 144, 255, 0.3)",
                animation: isListening ? "ripple 1.5s infinite" : "none",
            }}
            onMouseEnter={(e) => {
                if (!disabled) e.currentTarget.style.transform = "scale(1.08)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
            }}
        >
            {isListening ? (
                // Stop icon (square)
                <div style={{
                    width: "14px",
                    height: "14px",
                    backgroundColor: "white",
                    borderRadius: "2px",
                    transition: "all 0.3s ease",
                }} />
            ) : (
                // Mic icon
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M12 14C13.66 14 15 12.66 15 11V5C15 3.34 13.66 2 12 2C10.34 2 9 3.34 9 5V11C9 12.66 10.34 14 12 14Z"
                        fill="white"
                    />
                    <path
                        d="M17 11C17 13.76 14.76 16 12 16C9.24 16 7 13.76 7 11H5C5 14.53 7.61 17.43 11 17.92V21H13V17.92C16.39 17.43 19 14.53 19 11H17Z"
                        fill="white"
                    />
                </svg>
            )}
        </div>
    );
};

const SendNotificationModal = ({ visible, onCancel, userId, userName, onSuccess }) => {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [language, setLanguage] = useState("en-IN");
    const [isListening, setIsListening] = useState(false);
    const [micSupported, setMicSupported] = useState(true);
    const [loading, setLoading] = useState(false);

    const [createNotification] = useMutation(CREATE_NEW_WEB_NOTIFICATION);
    const descriptionRef = useRef(null);
    const recognitionRef = useRef(recognition);
    const lastProcessedIndexRef = useRef(0);

    useEffect(() => {
        if (!recognition) {
            setMicSupported(false);
            return;
        }

        const rec = recognitionRef.current;
        rec.lang = language;

        let isMounted = true;

        rec.onresult = (event) => {
            if (!isMounted) return;

            let finalTranscript = "";

            for (let i = lastProcessedIndexRef.current; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalTranscript += transcript + " ";
                    lastProcessedIndexRef.current = i + 1;
                }
            }

            if (finalTranscript.trim()) {
                setDescription(prev => {
                    const combined = prev.trim() + " " + finalTranscript.trim();
                    return combined.trim();
                });
            }
        };

        rec.onerror = (e) => {
            if (!isMounted) return;
            console.error("Speech error:", e.error);
            setIsListening(false);
            lastProcessedIndexRef.current = 0;

            if (e.error === "not-allowed") {
                message.error("Microphone access denied");
            } else if (e.error !== "aborted" && e.error !== "no-speech") {
                message.error("Voice recognition failed");
            }
        };

        rec.onend = () => {
            if (isMounted) {
                setIsListening(false);
                lastProcessedIndexRef.current = 0;
            }
        };

        return () => {
            isMounted = false;
            try {
                rec.stop();
            } catch (e) {
                // Ignore
            }
        };
    }, [language]);

    const toggleListening = () => {
        if (!micSupported || !recognitionRef.current) {
            message.error("Voice recognition not supported");
            return;
        }

        if (isListening) {
            try {
                recognitionRef.current.stop();
                setIsListening(false);
                lastProcessedIndexRef.current = 0;
                message.success("Recording stopped");
            } catch (e) {
                console.error("Stop error:", e);
                setIsListening(false);
            }
        } else {
            try {
                lastProcessedIndexRef.current = 0;
                recognitionRef.current.start();
                setIsListening(true);
                message.info(`Listening in ${languages[language]}...`);
            } catch (e) {
                console.error("Start error:", e);
                message.error("Could not start recording");
                setIsListening(false);
            }
        }
    };

    const handleSend = async () => {
        if (!title.trim() || !description.trim()) {
            message.warning("Title and description are required");
            return;
        }

        if (isListening) {
            recognitionRef.current?.stop();
            setIsListening(false);
        }

        setLoading(true);
        try {
            const res = await createNotification({
                variables: {
                    data: [{
                        userId: Number(userId),
                        title: title.trim(),
                        notificationType: "WEBADMIN",
                        referenceId: 0,
                        description: description.trim(),
                    }],
                },
            });

            if (res.data?.createNewWebNotification?.success) {
                message.success(`Notification sent to ${userName}!`);
                onSuccess?.();
                handleClose();
            } else {
                message.error("Failed to send notification");
            }
        } catch (err) {
            message.error("Network error occurred");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (isListening) {
            try {
                recognitionRef.current?.stop();
            } catch (e) {
                // Ignore
            }
        }

        setTitle("");
        setDescription("");
        setLanguage("en-IN");
        setIsListening(false);
        lastProcessedIndexRef.current = 0;
        onCancel();
    };

    return (
        <Modal
            open={visible}
            onCancel={handleClose}
            footer={null}
            centered
            width={600}
            title={
                <div style={{ textAlign: "center", fontFamily: "Inter", fontWeight: 600, fontSize: 18 }}>
                    Send Notification to <span style={{ color: "#AD0101" }}>{userName || "User"}</span>
                </div>
            }
        >
            {/* <Spin style={{ color: '#AD0101' }} spinning={loading} tip="Sending notification..."> */}
            <div style={{ padding: "24px 0" }}>
                {/* Title Input */}
                <div style={{ marginBottom: 20 }}>
                    <label style={{
                        display: "block",
                        marginBottom: 10,
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#262626"
                    }}>
                        Notification Title
                    </label>
                    <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Enter a title..."
                        size="large"
                        style={{
                            borderRadius: 8,
                            fontSize: 15
                        }}
                    />
                </div>

                {/* Description with Integrated Voice Input */}
                <div style={{ marginBottom: 20 }}>
                    <label style={{
                        display: "block",
                        marginBottom: 10,
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#262626"
                    }}>
                        Description
                    </label>

                    <div style={{
                        position: "relative",
                        border: "1px solid #d9d9d9",
                        borderRadius: 8,
                        transition: "all 0.3s ease",
                        // background: isListening ? "#f0f5ff" : "white"
                    }}>
                        <TextArea
                            ref={descriptionRef}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={isListening ? "Listening... Speak now!" : "Type your message or click the mic to speak..."}
                            rows={6}
                            style={{
                                fontFamily: "Inter",
                                fontSize: 15,
                                border: "none",
                                borderRadius: 8,
                                paddingRight: "70px",
                                // paddingBottom: "50px",
                                background: "transparent",
                                resize: "none"
                            }}
                        />

                        {/* Voice Controls Overlay - Bottom Right */}
                        <div style={{
                            position: "absolute",
                            bottom: "12px",
                            right: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            background: isListening ? "rgba(255, 255, 255, 0.9)" : "transparent",
                            padding: isListening ? "8px 12px" : "0",
                            borderRadius: "24px",
                            transition: "all 0.3s ease"
                        }}>
                            {isListening && <VoiceVisualizer isListening={isListening} />}
                            <MicButton
                                isListening={isListening}
                                onClick={toggleListening}
                                disabled={!micSupported}
                            />
                        </div>
                    </div>

                    {/* Status Message */}
                    {isListening && (
                        <div style={{
                            marginTop: 10,
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: 13,
                            color: "#AD0101",
                            fontWeight: 500
                        }}>
                            <span style={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                background: "#AD0101",
                                animation: "blink 1s infinite"
                            }} />
                            Recording in {languages[language]}... Click mic to stop
                        </div>
                    )}
                </div>

                {/* Language Selector */}
                <div style={{ marginBottom: 28 }}>
                    <label style={{
                        display: "block",
                        marginBottom: 10,
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#262626"
                    }}>
                        Voice Language
                    </label>
                    <Select
                        value={language}
                        onChange={setLanguage}
                        style={{ width: "100%" }}
                        size="large"
                        disabled={isListening}
                    >
                        {Object.entries(languages).map(([code, name]) => (
                            <Option key={code} value={code}>{name}</Option>
                        ))}
                    </Select>
                    {isListening && (
                        <div style={{
                            fontSize: 12,
                            color: "#AD0101",
                            marginTop: 6,
                            fontWeight: 500
                        }}>
                            Stop recording to change language
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 12,
                    paddingTop: 8,
                    borderTop: "1px solid #f0f0f0"
                }}>
                    <button
                        className="cancelMessage"
                        onClick={handleClose}
                        disabled={loading}
                        style={{
                            padding: "10px 24px",
                            fontSize: 15,
                            fontWeight: 500
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className="sendMessage"
                        onClick={handleSend}
                        disabled={loading || !title.trim() || !description.trim()}
                        style={{
                            padding: "10px 28px",
                            fontSize: 15,
                            fontWeight: 500
                        }}
                    >
                        {loading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : "Send"}
                    </button>
                </div>
            </div>
            {/* </Spin> */}

            <style jsx>{`
                @keyframes ripple {
                    0% {
                        box-shadow: 0 3px 10px rgba(255, 77, 79, 0.4), 
                                    0 0 0 0 rgba(255, 77, 79, 0.4);
                    }
                    50% {
                        box-shadow: 0 3px 10px rgba(255, 77, 79, 0.4), 
                                    0 0 0 8px rgba(255, 77, 79, 0);
                    }
                    100% {
                        box-shadow: 0 3px 10px rgba(255, 77, 79, 0.4), 
                                    0 0 0 0 rgba(255, 77, 79, 0);
                    }
                }
                @keyframes blink {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.3; }
                }
            `}</style>
        </Modal>
    );
};

export default SendNotificationModal;